import ScienceRounded from '@mui/icons-material/ScienceRounded';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { useAuth } from '../../auth/AuthProvider';
import { DataTable } from '../../components/DataTable';
import { Money } from '../../components/Money';
import { PageHeader } from '../../components/states';
import { businessDate, dateTime } from '../../lib/format';
import { useShop } from '../../shop/ShopProvider';
import { usePrint } from '../sales/print/usePrint';
import { NoAccess } from '../shop/ui';
import {
  FOODPANDA_PINK,
  useFoodpanda,
  useFoodpandaEvents,
  useFoodpandaOffline,
  useFoodpandaOrders,
  type FoodpandaOrder,
} from './api';
import { KitchenTicket } from './KitchenTicket';
import { MenuTab } from './MenuTab';
import { PayoutsTab } from './PayoutsTab';
import { ReportsTab } from './ReportsTab';
import { OrderCard, OrderStatusChip } from './OrderCard';
import { SetupTab, SimulatorDialog } from './SetupTab';
import { StoreControls } from './StoreControls';

const OFFLINE_TEXT =
  'Foodpanda works online only: the server cannot be reached. Orders keep waiting on Foodpanda; connect to the internet to see and accept them.';

type TabId = 'orders' | 'history' | 'menu' | 'payouts' | 'reports' | 'setup' | 'activity';

/**
 * Foodpanda: the live order board (new → preparing → ready) with the store's open / pause / close,
 * past orders, the menu and item availability, set-up (connection, outlets, the simulator) and the
 * log of what was exchanged with Foodpanda.
 */
export function FoodpandaPage() {
  const { can } = useAuth();
  const { format } = useShop();
  const fp = useFoodpanda();
  const offline = useFoodpandaOffline();
  const [params, setParams] = useSearchParams();
  const tabs: { id: TabId; label: string; show: boolean }[] = [
    { id: 'orders', label: 'Live orders', show: true },
    { id: 'history', label: 'All orders', show: true },
    { id: 'menu', label: 'Menu', show: true },
    { id: 'payouts', label: 'Payouts', show: fp.canManage },
    { id: 'reports', label: 'Reports', show: can('reports.view') },
    { id: 'setup', label: 'Set-up', show: fp.canManage },
    { id: 'activity', label: 'Activity', show: true },
  ];
  const visible = tabs.filter((t) => t.show);
  const requested = params.get('tab');
  const tab: TabId = visible.some((t) => t.id === requested) ? (requested as TabId) : 'orders';

  if (!can('foodpanda.orders') && !can('foodpanda.manage'))
    return (
      <>
        <PageHeader title="Foodpanda" />
        <NoAccess what="Foodpanda orders" />
      </>
    );
  if (fp.loading) return <CircularProgress />;
  const status = fp.status;
  const plugin = status?.plugin;
  if (offline && !status)
    return (
      <>
        <PageHeader title="Foodpanda" />
        <Alert severity="warning">{OFFLINE_TEXT}</Alert>
      </>
    );
  if (!fp.licensed || !plugin)
    return (
      <>
        <PageHeader title="Foodpanda" />
        <Alert severity="info">
          Foodpanda is an add-on: orders from Foodpanda arrive here and become sales orders and
          invoices by themselves. Contact PackBytes to add it.
        </Alert>
      </>
    );

  const connection = status.connection;
  const simulator = connection?.environment === 'SIMULATOR';
  let body: ReactNode;
  if (tab === 'setup') body = <SetupTab status={status} />;
  else if (!connection || status.outlets.length === 0)
    body = (
      <Alert severity="info">
        {fp.canManage
          ? 'Finish the set-up first: save the settings and link a location to your Foodpanda outlet.'
          : 'Foodpanda is not set up yet. Ask the shop owner to finish the set-up.'}
      </Alert>
    );
  else if (tab === 'payouts') body = <PayoutsTab />;
  else if (tab === 'reports') body = <ReportsTab />;
  else if (tab === 'menu') body = <MenuTab status={status} canManage={fp.canManage} />;
  else if (tab === 'orders')
    body = (
      <>
        <StoreControls outlets={status.outlets} />
        <LiveOrders
          canOrder={fp.canOrder}
          defaultPrep={connection.defaultPrepMinutes}
          simulator={simulator && fp.canManage}
        />
      </>
    );
  else if (tab === 'history')
    body = (
      <OrderHistory
        canOrder={fp.canOrder}
        defaultPrep={connection.defaultPrepMinutes}
        simulator={simulator && fp.canManage}
      />
    );
  else body = <Activity />;

  return (
    <>
      <PageHeader
        title="Foodpanda"
        subtitle="Accept Foodpanda orders, tell Foodpanda when the food is ready, and they are invoiced on pick-up."
        actions={
          simulator && fp.canManage && status.outlets.length > 0 ? <TestOrderButton /> : undefined
        }
      />
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        {offline && <Alert severity="warning">{OFFLINE_TEXT}</Alert>}
        {plugin.state === 'EXPIRED' && (
          <Alert severity="error">
            Foodpanda subscription ended, grace used. New orders are refused. Contact PackBytes to
            renew it.
          </Alert>
        )}
        {plugin.state === 'GRACE' && plugin.graceEndsAt && (
          <Alert severity="warning">
            The Foodpanda period has ended; it keeps working until{' '}
            {businessDate(plugin.graceEndsAt.slice(0, 10), format)}. Contact PackBytes to renew it.
          </Alert>
        )}
        {!plugin.shopEnabled && (
          <Alert severity="warning">
            Foodpanda is switched off for this shop: new orders are refused.
            {fp.canManage ? ' Switch it on under Set-up.' : ''}
          </Alert>
        )}
        {simulator && (
          <Alert severity="info" icon={<ScienceRounded />}>
            Simulator mode: nothing is sent to Foodpanda. Send test orders to try the whole flow.
          </Alert>
        )}
      </Stack>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2.5 }}>
        <Tabs
          value={tab}
          onChange={(_, v: TabId) => setParams(v === 'orders' ? {} : { tab: v })}
          variant="scrollable"
        >
          {visible.map((t) => (
            <Tab
              key={t.id}
              value={t.id}
              label={
                t.id === 'orders' && status.openOrders > 0 ? (
                  <Badge
                    badgeContent={status.openOrders}
                    sx={{
                      '& .MuiBadge-badge': { bgcolor: FOODPANDA_PINK, color: '#fff', right: -12 },
                    }}
                  >
                    {t.label}
                  </Badge>
                ) : (
                  t.label
                )
              }
            />
          ))}
        </Tabs>
      </Box>
      {body}
    </>
  );
}

function TestOrderButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outlined" startIcon={<ScienceRounded />} onClick={() => setOpen(true)}>
        Send test order
      </Button>
      {open && <SimulatorDialog onClose={() => setOpen(false)} />}
    </>
  );
}

function useKitchenPrint() {
  const { me } = useAuth();
  const { print, element } = usePrint();
  return {
    element,
    onPrint: (order: FoodpandaOrder) =>
      print('80mm', <KitchenTicket order={order} shopName={me?.tenant.name ?? ''} />),
  };
}

const COLUMNS: { title: string; statuses: FoodpandaOrder['status'][]; empty: string }[] = [
  { title: 'New', statuses: ['RECEIVED', 'FAILED'], empty: 'No new orders.' },
  { title: 'Preparing', statuses: ['ACCEPTED'], empty: 'Nothing being prepared.' },
  { title: 'Ready for pick-up', statuses: ['READY'], empty: 'Nothing waiting for a rider.' },
];

/** The live board: refreshes every few seconds. */
function LiveOrders(props: { canOrder: boolean; defaultPrep: number; simulator: boolean }) {
  const orders = useFoodpandaOrders({ status: 'RECEIVED,FAILED,ACCEPTED,READY' });
  const kitchen = useKitchenPrint();
  if (orders.isLoading) return <CircularProgress />;
  const rows = orders.data?.data ?? [];
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(3, minmax(0,1fr))' },
        alignItems: 'start',
      }}
    >
      {COLUMNS.map((col) => {
        const list = rows
          .filter((o) => col.statuses.includes(o.status))
          .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
        return (
          <Card
            key={col.title}
            component="section"
            aria-label={col.title}
            sx={{ p: 1.5, bgcolor: 'action.hover' }}
            elevation={0}
          >
            <Typography variant="subtitle2" sx={{ px: 0.5, mb: 1 }}>
              {col.title} ({list.length})
            </Typography>
            <Stack spacing={1.5}>
              {list.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 0.5, pb: 1 }}>
                  {col.empty}
                </Typography>
              ) : (
                list.map((o) => (
                  <OrderCard key={o.id} order={o} onPrint={kitchen.onPrint} {...props} />
                ))
              )}
            </Stack>
          </Card>
        );
      })}
      {kitchen.element}
    </Box>
  );
}

/** Every order, newest first; picked-up orders with a problem keep their actions. */
function OrderHistory(props: { canOrder: boolean; defaultPrep: number; simulator: boolean }) {
  const { format } = useShop();
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<FoodpandaOrder | null>(null);
  const orders = useFoodpandaOrders({ q: q || undefined });
  const kitchen = useKitchenPrint();
  const rows = orders.data?.data ?? [];
  const current = selected ? (rows.find((o) => o.id === selected.id) ?? selected) : null;
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,1fr) 380px' },
        alignItems: 'start',
      }}
    >
      <Box>
        <TextField
          size="small"
          label="Search order number or customer"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ mb: 1.5, width: { xs: '100%', sm: 320 } }}
        />
        <DataTable
          rows={rows}
          rowKey={(o) => o.id}
          loading={orders.isLoading}
          onRowClick={setSelected}
          empty="No Foodpanda orders yet."
          columns={[
            { key: 'code', header: 'Order', render: (o) => `#${o.code}` },
            { key: 'when', header: 'Received', render: (o) => dateTime(o.createdAt, format) },
            { key: 'customer', header: 'Customer', render: (o) => o.customerName ?? '—' },
            {
              key: 'status',
              header: 'Status',
              render: (o) => (
                <Stack direction="row" spacing={0.5}>
                  <OrderStatusChip status={o.status} />
                  {(o.error ?? o.needsRefund) && (
                    <Typography variant="caption" color="error">
                      {o.needsRefund ? 'Refund' : 'Problem'}
                    </Typography>
                  )}
                </Stack>
              ),
            },
            { key: 'invoice', header: 'Invoice', render: (o) => o.invoice?.number ?? '—' },
            {
              key: 'total',
              header: 'Total',
              align: 'right',
              render: (o) => <Money value={o.total} />,
            },
          ]}
        />
      </Box>
      {current && <OrderCard order={current} onPrint={kitchen.onPrint} {...props} />}
      {kitchen.element}
    </Box>
  );
}

function Activity() {
  const { format } = useShop();
  const events = useFoodpandaEvents();
  return (
    <DataTable
      rows={events.data ?? []}
      rowKey={(e) => e.id}
      loading={events.isLoading}
      empty="Nothing exchanged with Foodpanda yet."
      columns={[
        { key: 'when', header: 'When', render: (e) => dateTime(e.createdAt, format) },
        {
          key: 'direction',
          header: 'Direction',
          render: (e) => (e.direction === 'IN' ? 'From Foodpanda' : 'To Foodpanda'),
        },
        { key: 'action', header: 'What', render: (e) => e.action },
        {
          key: 'ok',
          header: 'Result',
          render: (e) =>
            e.ok ? (
              'OK'
            ) : (
              <Typography variant="body2" color="error">
                {e.error ?? 'Failed'}
              </Typography>
            ),
        },
      ]}
    />
  );
}
